const LEGACY_CHUNK_RETRY_KEY = "__chunk_reload_once__";

export const HARD_RELOAD_ATTEMPTS_KEY = "__app_hard_reload_attempts__";
export const DEFAULT_MAX_HARD_RELOADS = 3;

const BUST_QUERY_KEYS = ["_chunk", "_r"];

export function isChunkLoadFailureMessage(message) {
  if (typeof message !== "string") return false;
  const lower = message.toLowerCase();
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("Failed to load module script") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Loading chunk") ||
    message.includes("ChunkLoadError") ||
    message.includes("Unable to preload CSS") ||
    (lower.includes("dynamically imported module") && lower.includes("fail"))
  );
}

function isLikelyBuildAssetUrl(url) {
  try {
    const u = new URL(
      url,
      typeof window !== "undefined" ? window.location.origin : undefined
    );
    // Matches CRA (/static/js or /static/css) and Vite (/assets) build outputs
    return /\/(static\/(js|css)|assets)\/[^/]+\.(js|css)([?#]|$)/i.test(
      u.pathname
    );
  } catch {
    return false;
  }
}

export function isAssetLoadErrorEvent(event) {
  const t = event.target;
  if (t instanceof HTMLScriptElement && t.src) {
    return isLikelyBuildAssetUrl(t.src);
  }
  if (t instanceof HTMLLinkElement && t.href && t.rel === "stylesheet") {
    return isLikelyBuildAssetUrl(t.href);
  }
  return false;
}

export function getHardReloadAttemptCount() {
  if (typeof window === "undefined") return 0;
  try {
    const v = sessionStorage.getItem(HARD_RELOAD_ATTEMPTS_KEY);
    const n = parseInt(v ?? "0", 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * Cache-busting hard reload, capped at maxAttempts per tab session.
 * Adds ?_chunk=<timestamp> so the browser re-fetches the updated manifest.
 */
export function reloadWithCacheBustIfUnderMax(maxAttempts = DEFAULT_MAX_HARD_RELOADS) {
  if (typeof window === "undefined") return false;
  // When offline every script request fails with a network error that looks
  // like a chunk error — skip reload entirely and let OfflineOverlay handle it.
  if (!navigator.onLine) return false;
  const current = getHardReloadAttemptCount();
  if (current >= maxAttempts) return false;
  try {
    sessionStorage.setItem(HARD_RELOAD_ATTEMPTS_KEY, String(current + 1));
  } catch {
    return false;
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_chunk", Date.now().toString());
  window.location.replace(url.toString());
  return true;
}

function clearRecoverySessionState() {
  try {
    sessionStorage.removeItem(HARD_RELOAD_ATTEMPTS_KEY);
    sessionStorage.removeItem(LEGACY_CHUNK_RETRY_KEY);
  } catch {
    /* ignore */
  }
  const url = new URL(window.location.href);
  let changed = false;
  for (const k of BUST_QUERY_KEYS) {
    if (url.searchParams.has(k)) {
      url.searchParams.delete(k);
      changed = true;
    }
  }
  if (changed) {
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
  }
}

/**
 * After the app boots cleanly, reset retry counters so the next deploy
 * can auto-recover again.
 */
export function scheduleRecoveryStateReset(resetDelayMs = 4000) {
  if (typeof window === "undefined") return;
  window.addEventListener("load", () => {
    window.setTimeout(clearRecoverySessionState, resetDelayMs);
  });
}

/**
 * Installs window-level listeners for script/CSS 404s and unhandled chunk
 * promise rejections — catches errors that never reach React's error boundary.
 */
export function installGlobalChunkLoadRecovery(maxAttempts = DEFAULT_MAX_HARD_RELOADS) {
  if (typeof window === "undefined") return;

  // capture:true catches asset load errors on <script>/<link> before React sees them
  window.addEventListener(
    "error",
    (event) => {
      const msg = (event.error && event.error.message) || event.message || "";
      if (isAssetLoadErrorEvent(event) || isChunkLoadFailureMessage(msg)) {
        reloadWithCacheBustIfUnderMax(maxAttempts);
      }
    },
    true
  );

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const msg = (reason && reason.message) || String(reason != null ? reason : "");
    if (isChunkLoadFailureMessage(msg)) {
      reloadWithCacheBustIfUnderMax(maxAttempts);
    }
  });
}
